import type * as Types from '@openx/types/gqlTypes';
import type { AudienceShare } from '../useUpdateAndActivate';

export const prepareSharesPayloads = (
  initialShares: AudienceShare[],
  updatedShares: AudienceShare[],
  initialSharesRecipientToShareUid: Map<string, string>
) => {
  const initialSharesMap = new Map<string, AudienceShare>();
  const updatedSharesMap = new Map<string, AudienceShare>();

  initialShares.forEach(share => {
    initialSharesMap.set(share.recipient_account_id, share);
  });

  updatedShares.forEach(share => {
    updatedSharesMap.set(share.recipient_account_id, share);
  });

  const { audienceShareDeletePayload, removedRecipientIds } = getDeleteShares(
    initialShares,
    updatedSharesMap,
    initialSharesRecipientToShareUid
  );
  const { audienceShareUpdatePayload, updatedRecipientIds } = getUpdateShares(
    initialShares,
    updatedSharesMap,
    removedRecipientIds,
    initialSharesRecipientToShareUid
  );
  const audienceShareCreatePayload = getCreateShares(
    updatedShares,
    initialSharesMap,
    removedRecipientIds,
    updatedRecipientIds
  );

  return {
    audienceShareCreatePayload,
    audienceShareUpdatePayload,
    audienceShareDeletePayload,
  };
};

export const getDeleteShares = (
  initialShares: AudienceShare[],
  updatedSharesMap: Map<string, AudienceShare>,
  initialSharesRecipientToShareUid: Map<string, string>
) => {
  const removedRecipientIds = new Set<string>();

  const audienceShareDelete = initialShares.filter(share => {
    const recipientId = share.recipient_account_id;
    const updatedShare = updatedSharesMap.get(recipientId);

    if (!updatedShare) {
      removedRecipientIds.add(recipientId);
      return true;
    }

    if (share.revenue_method !== updatedShare.revenue_method) {
      return true;
    }

    if (share.revenue_method === 'CPM' && share.cpm_cap !== updatedShare.cpm_cap) {
      return true;
    }

    return false;
  });

  const audienceShareDeletePayload = audienceShareDelete.map(share => {
    const recipientId = share.recipient_account_id;

    return initialSharesRecipientToShareUid.get(recipientId) ?? '';
  });

  return { audienceShareDeletePayload, removedRecipientIds };
};

export const getUpdateShares = (
  initialShares: AudienceShare[],
  updatedSharesMap: Map<string, AudienceShare>,
  removedRecipientIds: Set<string>,
  initialSharesRecipientToShareUid: Map<string, string>
) => {
  const updatedRecipientIds = new Set<string>();

  const audienceShareUpdate = initialShares.filter(share => {
    const recipientId = share.recipient_account_id;

    if (removedRecipientIds.has(recipientId)) {
      return false;
    }

    const updatedShare = updatedSharesMap.get(recipientId);

    if (
      share.revenue_method === 'PoM' &&
      updatedShare?.revenue_method === 'PoM' &&
      share.third_party_revenue !== updatedShare?.third_party_revenue
    ) {
      updatedRecipientIds.add(recipientId);
      return true;
    }

    return false;
  });

  const audienceShareUpdatePayload: Types.AudienceShareUpdateParams[] = [];

  audienceShareUpdate.forEach(share => {
    const recipientId = share.recipient_account_id;
    const shareUid = initialSharesRecipientToShareUid.get(recipientId);
    const updatedShare = updatedSharesMap.get(recipientId);

    if (shareUid && updatedShare) {
      audienceShareUpdatePayload.push({
        uid: shareUid,
        third_party_revenue: updatedShare.third_party_revenue ?? '0',
      });
    }
  });

  return { audienceShareUpdatePayload, updatedRecipientIds };
};

export const getCreateShares = (
  updatedShares: AudienceShare[],
  initialSharesMap: Map<string, AudienceShare>,
  removedRecipientIds: Set<string>,
  updatedRecipientIds: Set<string>
) => {
  const audienceShareCreatePayload = updatedShares.filter(share => {
    const recipientId = share.recipient_account_id;
    const initialShare = initialSharesMap.get(recipientId);

    if (removedRecipientIds.has(recipientId)) {
      return false;
    }

    if (updatedRecipientIds.has(recipientId)) {
      return false;
    }

    if (share.revenue_method !== initialShare?.revenue_method) {
      return true;
    }

    if (share.revenue_method === 'CPM' && share.cpm_cap !== initialShare?.cpm_cap) {
      return true;
    }

    return !initialSharesMap.has(recipientId);
  });

  return audienceShareCreatePayload;
};
